<template>
	<div class="performance-card">
		<div class="performance-card__header">
			<div class="performance-card__header-left">
				<div class="performance-card__icon-box">
					<i class="fas fa-trophy"></i>
				</div>
				<div>
					<h2 class="performance-card__title">Your Performance</h2>
					<p class="performance-card__subtitle">Track your progress and benchmarks</p>
				</div>
			</div>
		</div>
		<div v-if="loading" class="performance-card__loading">
			<b-skeleton height="96px" width="100%" class="mb-3"></b-skeleton>
			<b-skeleton height="24px" width="40%" class="mb-2"></b-skeleton>
			<b-skeleton height="60px" width="100%" class="mb-2"></b-skeleton>
			<b-skeleton height="60px" width="100%" class="mb-2"></b-skeleton>
			<b-skeleton height="60px" width="100%"></b-skeleton>
		</div>

		<template v-else>
			<div class="performance-card__stats">
				<div class="performance-card__stat">
					<i class="fas fa-clipboard-list performance-card__stat-icon"></i>
					<p class="performance-card__stat-label">ASSIGNMENTS ATTEMPTED</p>
					<p class="performance-card__stat-value">
						<strong>{{ assignmentsAttempted }}</strong>
						<span class="performance-card__stat-denom">/{{ assignmentsTotal }}</span>
					</p>
				</div>
				<div class="performance-card__stat">
					<i class="fas fa-bullseye performance-card__stat-icon"></i>
					<p class="performance-card__stat-label">AVERAGE SCORE</p>
					<p class="performance-card__stat-value">
						<strong>{{ avgCombinedScore }}</strong><span class="performance-card__stat-pct">%</span>
					</p>
				</div>
				<div class="performance-card__stat">
					<i class="fas fa-award performance-card__stat-icon"></i>
					<p class="performance-card__stat-label">AVERAGE EXAM SCORE</p>
					<p class="performance-card__stat-value">
						<strong>{{ avgExamScore }}</strong><span class="performance-card__stat-pct">%</span>
					</p>
				</div>
			</div>

			<hr class="performance-card__divider" />

			<div class="performance-card__scores">
				<h3 class="performance-card__scores-title">Assignment & Exam Scores</h3>
				<p v-if="!gradedAssignments.length && !examScores.length" class="performance-card__no-data">
					No results available yet.
				</p>
				<div
					v-for="item in gradedAssignments"
					:key="'a-' + item.content.id"
					class="performance-card__score-row"
				>
					<span class="performance-card__score-dot"></span>
					<div class="performance-card__score-info">
						<p class="performance-card__score-name">{{ item.content.title }}</p>
						<p class="performance-card__score-date">{{ formatDate(item.returnedAt) }}</p>
					</div>
					<span class="performance-card__score-value">
						{{ item.marks }}<span class="performance-card__score-denom">/{{ assignmentTotal(item) }}</span>
					</span>
				</div>
				<div
					v-for="item in examScores"
					:key="'e-' + item.id"
					class="performance-card__score-row"
				>
					<span class="performance-card__score-dot"></span>
					<div class="performance-card__score-info">
						<p class="performance-card__score-name">{{ item.content.title }}</p>
						<p class="performance-card__score-date">{{ formatDate(item.examDate) }}</p>
					</div>
					<span class="performance-card__score-value">
						{{ item.totalMarksObtained }}<span class="performance-card__score-denom">/{{ item.totalMarks }}</span>
					</span>
				</div>
			</div>
		</template>

	</div>
</template>

<script>
import { mapActions } from "vuex";

export default {
	name: "CourseDetailPerformance",
	props: {
		productVariantId: { type: [String, Number], required: true },
		productVariant:Object
	},
	data() {
		return {
			loading: true,
			assignmentScores: [],
			examScores: []
		};
	},
	computed: {
		assignmentsAttempted() {
			return this.assignmentScores.filter((s) => s.isSubmitAssignment).length;
		},
		assignmentsTotal() {
			return this.assignmentScores.length;
		},
		avgCombinedScore() {
			const gradedAssignmentPercentages = this.assignmentScores
				.filter((assignment) => assignment.isGraded)
				.map((assignment) => Number(assignment.percentage || 0));
			const examPercentages = this.examScores
				.map((exam) => Number(exam.totalScorePercentage || 0));
			const allPercentages = [...gradedAssignmentPercentages, ...examPercentages];
			if (!allPercentages.length) return 0;
			const total = allPercentages.reduce((sum, pct) => sum + pct, 0);
			return Math.round(total / allPercentages.length);
		},
		avgExamScore() {
			if (!this.examScores.length) return 0;
			return Math.round(this.examScores.reduce((sum, s) => sum + Number(s.totalScorePercentage || 0), 0) / this.examScores.length);
		},
		gradedAssignments() {
			return this.assignmentScores.filter((s) => s.isGraded);
		},
		assignmentScoreTags() {
        const showAssignmentScoreByTags = this.productVariant?.properties?.dashboardPageConfig_showAssignmentScoreByTags ? JSON.parse(this.productVariant?.properties?.dashboardPageConfig_showAssignmentScoreByTags) : null;
    		return showAssignmentScoreByTags || [];
		},
		examScoreTags() {
        const showExamScoreByTags = this.productVariant?.properties?.dashboardPageConfig_showExamScoreByTags ? JSON.parse(this.productVariant?.properties?.dashboardPageConfig_showExamScoreByTags) : null;
    		return showExamScoreByTags || [];
		},
    filterAssignmentsByTag() {
      return this.assignmentScoreTags?.length > 0 ?? false;
    },
    filterExamsByTag() {
      return this.examScoreTags?.length > 0 ?? false;
    }
	},
	methods: {
		...mapActions("assignment", ["getProductvariantAssignmentResults"]),
		...mapActions("exam", ["getExamResultByProductVariant"]),
		assignmentTotal(item) {
			try { return JSON.parse(item.content.extraProperties).Marks; } catch { return null; }
		},
		formatDate(dateStr) {
			if (!dateStr) return "";
			return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
		}
	},
	async created() {
		const id = this.productVariantId;
		try {
			const [assignmentResults, examResults] = await Promise.all([
				this.getProductvariantAssignmentResults({ productvariantId: id, filterByTag: this.filterAssignmentsByTag, tags:this.assignmentScoreTags }),
				this.getExamResultByProductVariant({ productVariantID: id, filterByTag: this.filterExamsByTag, tags: this.examScoreTags }),
			]);
			this.assignmentScores = assignmentResults || [];
			this.examScores = examResults || [];
		} catch (e) {
			console.error("CourseDetailPerformance error:", e);
		} finally {
			this.loading = false;
		}
	},
};
</script>

<style scoped>
.performance-card {
	background: #fff;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	padding: 24px 28px;
}
.performance-card__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 20px;
}

.performance-card__header-left {
	display: flex;
	align-items: center;
	gap: 14px;
}

.performance-card__icon-box {
	width: 48px;
	height: 48px;
	border-radius: 12px;
	background: linear-gradient(135deg, #ef4444, #b91c1c);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	color: #fff;
	font-size: 20px;
}

.performance-card__title {
	font-size: 20px;
	font-weight: 700;
	color: #111827;
	margin: 0 0 2px;
}

.performance-card__subtitle {
	font-size: 13px;
	color: #6b7280;
	margin: 0;
}
.performance-card__stats {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 16px;
	margin-bottom: 20px;
}

.performance-card__stat {
	border: 1px solid var(--border-color);
	border-radius: 12px;
	padding: 16px 18px;
}

.performance-card__stat-icon {
	font-size: 22px;
	margin-bottom: 8px;
	display: block;
  color: var(--primary-color, #dc2626);
}

.performance-card__stat-label {
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.05em;
	color: #6b7280;
	margin: 0 0 6px;
}

.performance-card__stat-value {
	font-size: 28px;
	font-weight: 700;
	color: #111827;
	margin: 0;
	line-height: 1;
}

.performance-card__stat-denom,
.performance-card__stat-pct {
	font-size: 18px;
	font-weight: 500;
	color: #6b7280;
}
.performance-card__divider {
	border: none;
	border-top: 1px solid #e5e7eb;
	margin: 4px 0 20px;
}
.performance-card__scores-title {
	font-size: 15px;
	font-weight: 700;
	color: #111827;
	margin: 0 0 12px;
}

.performance-card__no-data {
	color: #9ca3af;
	font-size: 14px;
	text-align: center;
	padding: 24px 0;
}

.performance-card__score-row {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 14px 0;
	border-bottom: 1px solid #f3f4f6;
}

.performance-card__score-row:last-child {
	border-bottom: none;
}

.performance-card__score-dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	flex-shrink: 0;
	background: var(--primary-color, #dc2626);
}

.performance-card__score-info {
	flex: 1;
	min-width: 0;
}

.performance-card__score-name {
	font-size: 14px;
	font-weight: 600;
	color: #111827;
	margin: 0 0 2px;
}

.performance-card__score-date {
	font-size: 12px;
	color: #6b7280;
	margin: 0;
}

.performance-card__score-value {
	font-size: 18px;
	font-weight: 700;
	flex-shrink: 0;
	color: var(--primary-color, #dc2626);
}

.performance-card__score-denom {
	font-size: 13px;
	font-weight: 500;
	color: #9ca3af;
}
.performance-card__loading { padding-top: 4px; }
@media (max-width: 768px) {
	.performance-card { padding: 18px 16px; }
	.performance-card__stats { grid-template-columns: 1fr; gap: 10px; }
	.performance-card__stat-value { font-size: 24px; }
}
</style>
